<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { listSalesOrders } from '../productService'
import type { SalesOrderOption } from '../types'

const props = defineProps<{
  open: boolean
  title: string
}>()

const emit = defineEmits<{
  close: []
  select: [order: SalesOrderOption]
}>()

const client = useApiClient()
const search = ref('')
const orders = ref<SalesOrderOption[]>([])
const loading = ref(false)
const errorMessage = ref('')
const page = ref(1)
const pageSize = 8
const totalPages = computed(() => Math.max(1, Math.ceil(orders.value.length / pageSize)))
const visibleOrders = computed(() => {
  const start = (page.value - 1) * pageSize
  return orders.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleOrders.value.map((order) => ({
    id: order.id,
    title: order.so_code || `Order #${order.id}`,
    subtitle: order.customer_name || 'No customer',
    meta: order.status,
  })),
)
let searchTimer: ReturnType<typeof setTimeout> | undefined

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    orders.value = await listSalesOrders(client, search.value.trim() || undefined)
    page.value = Math.min(page.value, Math.max(1, Math.ceil(orders.value.length / pageSize)))
  } catch (error: unknown) {
    orders.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
    void load()
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  clearTimeout(searchTimer)
  page.value = 1
  searchTimer = setTimeout(load, 300)
})

function selectOption(option: SmartPickerOption): void {
  const order = visibleOrders.value.find((entry) => entry.id === Number(option.id))
  if (order) emit('select', order)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    eyebrow="Sales orders"
    search-label="Search sales orders"
    search-placeholder="Order code or customer"
    :search-value="search"
    :loading="loading"
    :error-message="errorMessage"
    empty-message="No matching sales orders found."
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="`${orders.length} items · Page ${page} of ${totalPages}`"
    close-aria-label="Close sales order picker"
    retry-label="Retry"
    previous-label="Previous"
    next-label="Next"
    cancel-label="Cancel"
    @close="emit('close')"
    @retry="load"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #loading>Loading sales orders...</template>
  </SmartPickerModal>
</template>
